import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Apollo} from 'apollo-angular';
import {Observable, throwError} from 'rxjs';
import {HttpLink} from 'apollo-angular/http';
import {DefaultOptions, InMemoryCache} from '@apollo/client/core';
import {catchError, map} from 'rxjs/operators';
import {Store} from '@ngxs/store';
import {GRAPHQL, GRAPHQL_COMMON, INVOICE_GRAPHQL, REPORT_GRAPHQL} from '@configs/api.config';

@Injectable({
  providedIn: 'root'
})
export class GraphqlClientService {
  constructor(
    private httpClient: HttpClient,
    private gqlClient: Apollo,
    private httpLink: HttpLink,
    private store: Store) {
  }

  createLinkWithGQLServer(): void {

    const defaultOptions: DefaultOptions = {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    };

    const {token} = this.store.selectSnapshot(store => store.auth);

    const graphql: any = {uri: GRAPHQL};

    if (this.gqlClient) {
      this.gqlClient.removeClient('invoice-endpoint');
      this.gqlClient.removeClient('report-endpoint');
      this.gqlClient.removeClient('gql-common-endpoint');
      this.gqlClient.removeClient();
    }

    this.gqlClient.createDefault({
      headers: {
        Authorization: `Bearer ${token}`
      },
      link: this.httpLink.create(graphql),
      cache: new InMemoryCache({
        addTypename: false
      }),
      defaultOptions
    });

    const invoice: any = {uri: INVOICE_GRAPHQL};

    this.gqlClient.createNamed('invoice-endpoint', {
      headers: {
        Authorization: `Bearer ${token}`
      },
      link: this.httpLink.create(invoice),
      cache: new InMemoryCache({
        addTypename: false
      }),
      defaultOptions
    });

    const report: any = {uri: REPORT_GRAPHQL};

    this.gqlClient.createNamed('report-endpoint', {
      headers: {
        Authorization: `Bearer ${token}`
      },
      link: this.httpLink.create(report),
      cache: new InMemoryCache({
        addTypename: false
      }),
      defaultOptions
    });

    
    const common: any = {uri: GRAPHQL_COMMON};
    this.gqlClient.createNamed('gql-common-endpoint', {
      headers: {
        Authorization: `Bearer ${token}`
      },
      link: this.httpLink.create(common),
      cache: new InMemoryCache({
        addTypename: false
      }),
      defaultOptions
    });
  }

  /**
   * Shared unwrapping for every GraphQL call in this service.
   *
   * All seven call sites previously did `map(result => result.data[queryName])`
   * followed by a `catchError` that returned the error object. Three problems
   * with that, all fixed here:
   *
   *  1. `result.errors` was discarded on every endpoint except gql-common. The
   *     clients are configured with `errorPolicy: 'all'`, which means Apollo
   *     deliberately hands back BOTH data and errors — so a failed field arrived
   *     as a silent null and nothing said why.
   *
   *  2. `result.data[queryName]` throws `Cannot read properties of null` when
   *     `data` itself is null, which is what Apollo returns when a root field
   *     errors. The crash then surfaced far from its cause.
   *
   *  3. `catchError(err => return err)` returns an Error where RxJS requires an
   *     ObservableInput. That is not a caught error — it makes RxJS raise its own
   *     "invalid object where a stream was expected", losing the original error
   *     entirely. Rethrowing lets callers' error handlers actually receive it.
   *
   * This matters more since the gateway stopped returning failed fetches as
   * successful-looking responses full of nulls: real errors now arrive as real
   * errors, and this service is where they were being dropped.
   */
  private unwrap<Type>(queryName: string, operation: 'query' | 'mutation') {
    return (source: Observable<any>): Observable<Type> =>
      source.pipe(
        map(result => {
          if (result?.errors?.length > 0) {
            console.error(
              `[gql] ${operation} ${queryName} returned ${result.errors.length} error(s): ` +
              result.errors.map(e => e?.message).join('; ')
            );
          }
          // `data` is null when a root field errors under errorPolicy 'all'.
          return (result?.data ? result.data[queryName] : null) as Type;
        }),
        catchError(err => {
          console.error(`[gql] ${operation} ${queryName} failed:`, err);
          return throwError(() => err);
        })
      );
  }

  queryDataFromGQL<Type>(variables: object, query, queryName: string, endpoint?: string): Observable<Type> {
    if (endpoint === 'invoice-endpoint') {
      return this.gqlClient.use('invoice-endpoint')
        .watchQuery<Type>(
          {
            query,
            variables,
            errorPolicy: 'all'
          }
        ).valueChanges
        .pipe(this.unwrap<Type>(queryName, 'query'));
    } else if (endpoint === 'report-endpoint') {
      return this.gqlClient.use('report-endpoint')
        .watchQuery<Type>(
          {
            query,
            variables,
            errorPolicy: 'all'
          }
        ).valueChanges
        .pipe(this.unwrap<Type>(queryName, 'query'));
    } else if (endpoint === 'gql-common-endpoint') {
      return this.gqlClient.use('gql-common-endpoint')
        .watchQuery<Type>(
          {
            query,
            variables,
            errorPolicy: 'all'
          }
        ).valueChanges
        .pipe(this.unwrap<Type>(queryName, 'query'));
    } else {
      return this.gqlClient
        .watchQuery<Type>(
          {
            query,
            variables,
            errorPolicy: 'all'
          }
        ).valueChanges
        .pipe(this.unwrap<Type>(queryName, 'query'));
    }
  }

  mutateDataFromGQL<Type>(variables: object, query, queryName: string, endpoint?: string): Observable<Type> {

    if (endpoint === 'invoice-endpoint') {
      return this.gqlClient.use(endpoint)
        .mutate(
          {
            mutation: query,
            variables,
            errorPolicy: 'all'
          }
        )
        .pipe(this.unwrap<Type>(queryName, 'mutation'));
    } else if (endpoint === 'report-endpoint') {
      return this.gqlClient.use('report-endpoint')
        .mutate(
          {
            mutation: query,
            variables,
            errorPolicy: 'all'
          }
        )
        .pipe(this.unwrap<Type>(queryName, 'mutation'));
    } else {
      return this.gqlClient
        .mutate(
          {
            mutation: query,
            variables,
            errorPolicy: 'all'
          }
        )
        .pipe(this.unwrap<Type>(queryName, 'mutation'));
    }
  }
}
